import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { stringFormat } from '@brycemarshall/string-format';
import { ClientInactivationEvent, MatchType, SearchCriteria } from '@models/configuration/model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { TopicSubTopicInformation } from 'src/app/shared/models/clux/model';
import { Model } from 'src/app/shared/models/clux/model-legacy';
import { ServiceFactory } from 'src/app/shared/services/service.factory';

import { RouteContextProviderService } from '../../../app/shared/utils/routecontextprovider.service';
import { CommonConstants } from '../constants/common.constant';
import { CoreService } from '../models/clux/enum';
import { Attachment, AttachmentUrlResponse } from '../models/uba/file/model';
import { Customization } from '../models/uba/profileConfiguration/model';
import { Uri } from '../utils/uri';
import { BrandService } from './brand.service';

export interface InternalStateType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  public routecontextprovider = inject(RouteContextProviderService);
  private serviceFactory = inject(ServiceFactory);
  private brandService = inject(BrandService);
  private http = inject(HttpClient);

  public showPastSchedule: boolean = false;
  public showPostManuallyGrowl: boolean = true;
  public growlProperties: Model.growlProps[] = [];
  public _state: InternalStateType = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public checkboxOption: any[] = [];
  public employmentInformation: Model.EmploymentInformation;
  public supportInformation: TopicSubTopicInformation;
  public resetEnrollPage = new Subject<void>();
  public stateNotification = new BehaviorSubject<boolean>(true);

  private readonly brand = this.brandService.getBrandResources();

  private messageSource: Subject<string | boolean | Model.EmitSelected> = new Subject<Model.EmitSelected | boolean | string>();

  private static readonly mimeTypeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
  };

  public resetPage(): void {
    this.resetEnrollPage.next();
  }

  public get(prop?: string): InternalStateType {
    /**
     * Use our state getter for the clone.
     */
    const state: InternalStateType = this._state;
    return Object.prototype.hasOwnProperty.call(state, prop) ? state[prop] : state;
  }

  public set(prop: string, value: string): string {
    /**
     * Internally mutate our state.
     */
    return (this._state[prop] = value);
  }

  public sendMessage(message: string): void {
    this.messageSource.next(message);
  }

  public getMessage(): Observable<Model.EmitSelected | boolean | string> {
    return this.messageSource.asObservable();
  }

  public setClearFilter(state: Model.EmitSelected | boolean): void {
    this.messageSource.next(state);
  }

  public getClearFilter(): Observable<string | boolean | Model.EmitSelected> {
    return this.messageSource.asObservable();
  }

  public getCustomizationByClientId(clientId: string): Observable<Model.SearchResults<Customization[]>> {
    const searchCriteria = [
      {
        key: 'parentId',
        value: clientId,
      },
    ];
    return this.serviceFactory.search<Customization[]>(
      CommonConstants.getPath.profile.configurationKey,
      CommonConstants.getPath.profile.getCustomization,
      searchCriteria,
      { profileId: clientId },
      {
        skip: 0,
        take: 1,
        orderBy: 'created',
        orderDirection: 'desc',
      }
    );
  }
  public getLogo(profileId: string, attachmentId: string): Observable<string> {
    const baseUrl: string = stringFormat(CommonConstants.getPath.file.downloadFile, {
      profileId,
      attachmentId,
    });
    return this.serviceFactory.query<Attachment>(CommonConstants.getPath.file.serviceKey, baseUrl).pipe(
      switchMap((attachment: Attachment) => {
        return this.getSignedURL(profileId, attachment).pipe(map((url: string | null) => url ?? ''));
      })
    );
  }

  public notifyState(currentStateStatus: boolean): void {
    this.stateNotification.next(currentStateStatus);
  }

  public getMessageByBrand(msg: string): string {
    return stringFormat(msg, { companyName: this.brand.companyName });
  }

  /**
   * Gets inactivation details that include scheduled inactivation date
   * which does not exist on the Client entity.
   */
  public getClientInactivationEvent(clientId: string): Observable<ClientInactivationEvent> {
    const criteria: SearchCriteria = [{ key: 'clientId', value: clientId, matchType: MatchType.EXACT }];

    const url = new Uri(`/profile/${clientId}/configuration/clientInactivationEvent/search`, CoreService.Configuration);
    return this.http.post<ClientInactivationEvent[]>(url.toString(), criteria).pipe(map((results) => results[0]));
  }

  private getSignedURL(profileId: string, attachment: Attachment): Observable<string | null> {
    const ext = attachment.attachmentType?.toLowerCase();
    const contentType = encodeURIComponent(SharedService.mimeTypeMap[ext] ?? `application/${ext}`);
    const baseUrl: string = stringFormat(CommonConstants.getPath.file.attachmentSignedUrl, {
      profileId,
      attachmentId: attachment.id,
      contentType,
      fileName: encodeURIComponent(attachment.friendlyFileName),
    });
    return this.serviceFactory.query<AttachmentUrlResponse>(CommonConstants.getPath.file.serviceKey, baseUrl).pipe(map((res) => (res?.sync && res?.url ? res.url : null)));
  }
}
